import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiLock, FiMail } from 'react-icons/fi';

import AuthLayout from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import { useAuth } from '../context/AuthContext';

const EMPTY = { email: '', password: '' };

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [remember, setRemember] = useState(true);

  const redirectTo = location.state?.from ?? '/';
  // Set by SignupPage-adjacent flows (e.g. "account created, now log in").
  const notice = location.state?.notice;

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const clientErrors = {};
    if (!values.email.trim()) clientErrors.email = 'Please enter your email address.';
    if (!values.password) clientErrors.password = 'Please enter your password.';

    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      await login({
        email: values.email.trim(),
        password: values.password,
        remember,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.body?.errors) {
        setErrors(err.body.errors);
      } else {
        // The server returns one generic message for bad credentials on
        // purpose — don't try to guess which field was wrong and highlight it.
        setFormError(
          err.status
            ? err.message
            : 'Couldn’t reach the server. Check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      aside={
        <>
          <h2 className="mm-auth__aside-title">Welcome back.</h2>
          <p className="mm-auth__aside-text">
            Pick up where you left off — your saved therapists, enquiries and
            upcoming sessions are all here.
          </p>

          <p className="mm-auth__aside-note">
            <strong>Going through a crisis?</strong> You don’t need an account to
            get help. Crisis support is available right now on the{' '}
            <Link to="/crisis" style={{ color: '#fff', textDecoration: 'underline' }}>
              crisis support page
            </Link>
            .
          </p>
        </>
      }
    >
      <h1 className="mm-auth__title">Log in</h1>
      <p className="mm-auth__subtitle">
        New here? <Link to="/signup">Create an account</Link>.
      </p>

      <form className="mm-auth__form" onSubmit={handleSubmit} noValidate>
        {notice && (
          <div className="mm-form-alert mm-form-alert--success" role="status">
            <FiCheckCircle size={16} aria-hidden="true" />
            <span>{notice}</span>
          </div>
        )}

        {formError && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{formError}</span>
          </div>
        )}

        <TextField
          label="Email address"
          name="email"
          type="email"
          icon={FiMail}
          placeholder="you@example.com"
          autoComplete="email"
          required
          value={values.email}
          onChange={handleChange}
          error={errors.email}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          icon={FiLock}
          placeholder="Your password"
          autoComplete="current-password"
          required
          value={values.password}
          onChange={handleChange}
          error={errors.password}
        />

        <div className="mm-form-row">
          <label className="mm-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>Keep me logged in</span>
          </label>

          <Link to="/forgot-password">Forgot password?</Link>
        </div>

        <Button type="submit" className="mm-auth__submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
              Logging in…
            </>
          ) : (
            'Log in'
          )}
        </Button>
      </form>

      <div className="mm-auth__footer">
        Are you a therapist?{' '}
        <Link to="/clinician/login">Log in to the clinician portal</Link>
      </div>
    </AuthLayout>
  );
}
